import { useEffect, useRef, useState } from "react";
import { jalaaliMonthLength, toGregorian, toJalaali } from "jalaali-js";
import SVG from "../SVG";
import Custom from "../Buttons/Custom";

const ITEM_HEIGHT = 44;
const WHEEL_HEIGHT = 220;
const EDGE_SPACER_HEIGHT = (WHEEL_HEIGHT - ITEM_HEIGHT) / 2;
const TEHRAN_TIME_ZONE = "Asia/Tehran";
const MONTH_NAMES = [
  "فروردین",
  "اردیبهشت",
  "خرداد",
  "تیر",
  "مرداد",
  "شهریور",
  "مهر",
  "آبان",
  "آذر",
  "دی",
  "بهمن",
  "اسفند",
];
const PERSIAN_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const TEHRAN_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: TEHRAN_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});
const PICKER_VARIANTS = {
  default: {
    wrapperClassName: "w-full flex flex-col gap-1",
    labelClassName: "text-sm text-zi-text-light/50 dark:text-zi-text-dark/50",
    buttonBaseClassName:
      "relative w-full rounded-md border p-1 text-right text-base outline-none transition text-zi-text-light dark:text-zi-text-dark border-zi-border-light dark:border-zi-border-dark",
    readOnlyButtonClassName: "cursor-default border-opacity-0 dark:border-opacity-0",
    editableButtonClassName:
      "border-opacity-100 dark:border-opacity-100 hocus:ring-1 hocus:ring-zi-border-light hocus:dark:ring-zi-border-dark hocus:bg-zi-bg1-light hocus:dark:bg-zi-bg1-dark",
    placeholderClassName: "text-zi-text-light/50 dark:text-zi-text-dark/50",
  },
  admin: {
    wrapperClassName: "w-full flex flex-col gap-2.5",
    labelClassName:
      "text-sm font-normal text-zi-text-light/50 dark:text-zi-text-dark/50",
    buttonBaseClassName:
      "relative w-full rounded-lg border border-zi-border-light px-2.5 py-1 text-right text-base text-zi-text-light dark:border-zi-border-dark dark:text-zi-text-dark appearance-none bg-transparent outline-none transition",
    readOnlyButtonClassName: "cursor-default",
    editableButtonClassName:
      "hocus:ring-1 hocus:ring-zi-border-light hocus:dark:ring-zi-border-dark",
    placeholderClassName: "text-zi-text-light/50 dark:text-zi-text-dark/50",
  },
};

function toPersianDigits(value) {
  return String(value).replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)]);
}

function parseGregorianDate(value) {
  if (!value) return null;

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  return {
    gy: Number(match[1]),
    gm: Number(match[2]),
    gd: Number(match[3]),
    hour: 0,
    minute: 0,
  };
}

function isValidGregorianDate({ gy, gm, gd }) {
  if (gm < 1 || gm > 12 || gd < 1 || gd > 31) {
    return false;
  }

  const date = new Date(Date.UTC(gy, gm - 1, gd));

  return (
    date.getUTCFullYear() === gy &&
    date.getUTCMonth() === gm - 1 &&
    date.getUTCDate() === gd
  );
}

function getDateTimeFormatterParts(date) {
  const parts = TEHRAN_DATE_TIME_FORMATTER.formatToParts(date);

  const readPart = (type) =>
    Number(parts.find((part) => part.type === type)?.value || 0);

  return {
    gy: readPart("year"),
    gm: readPart("month"),
    gd: readPart("day"),
    hour: readPart("hour"),
    minute: readPart("minute"),
  };
}

function parseValueAsTehranDateTime(value) {
  if (!value) return null;

  const dateOnlyValue = parseGregorianDate(value);
  if (dateOnlyValue) {
    return dateOnlyValue;
  }

  if (typeof value === "string") {
    const naiveUtcMatch = value.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/,
    );

    if (naiveUtcMatch) {
      return getDateTimeFormatterParts(
        new Date(
          Date.UTC(
            Number(naiveUtcMatch[1]),
            Number(naiveUtcMatch[2]) - 1,
            Number(naiveUtcMatch[3]),
            Number(naiveUtcMatch[4]),
            Number(naiveUtcMatch[5]),
            Number(naiveUtcMatch[6] || 0),
          ),
        ),
      );
    }
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return getDateTimeFormatterParts(parsedDate);
}

function convertGregorianToJalaliDateTime(value) {
  const parsedDate = parseValueAsTehranDateTime(value);

  if (
    !parsedDate ||
    !isValidGregorianDate({
      gy: parsedDate.gy,
      gm: parsedDate.gm,
      gd: parsedDate.gd,
    })
  ) {
    return null;
  }

  try {
    return {
      ...toJalaali(parsedDate.gy, parsedDate.gm, parsedDate.gd),
      hour: parsedDate.hour,
      minute: parsedDate.minute,
    };
  } catch {
    return null;
  }
}

function compareJalaliDates(a, b) {
  if (a.jy !== b.jy) return a.jy - b.jy;
  if (a.jm !== b.jm) return a.jm - b.jm;
  return a.jd - b.jd;
}

function compareTime(a, b) {
  if (a.hour !== b.hour) return a.hour - b.hour;
  return a.minute - b.minute;
}

function clampJalaliDate(date, minDate, maxDate) {
  const safeDay = Math.min(date.jd, jalaaliMonthLength(date.jy, date.jm));
  const safeDate = { ...date, jd: safeDay };

  if (compareJalaliDates(safeDate, minDate) < 0) {
    return { ...minDate };
  }

  if (compareJalaliDates(safeDate, maxDate) > 0) {
    return { ...maxDate };
  }

  return safeDate;
}

function clampJalaliDateTime(date, minDate, maxDate) {
  const clampedDate = clampJalaliDate(
    {
      ...date,
      hour: Math.min(Math.max(Number(date.hour) || 0, 0), 23),
      minute: Math.min(Math.max(Number(date.minute) || 0, 0), 59),
    },
    minDate,
    maxDate,
  );

  if (
    compareJalaliDates(clampedDate, minDate) === 0 &&
    compareTime(clampedDate, minDate) < 0
  ) {
    return {
      ...clampedDate,
      hour: minDate.hour,
      minute: minDate.minute,
    };
  }

  if (
    compareJalaliDates(clampedDate, maxDate) === 0 &&
    compareTime(clampedDate, maxDate) > 0
  ) {
    return {
      ...clampedDate,
      hour: maxDate.hour,
      minute: maxDate.minute,
    };
  }

  return clampedDate;
}

function getDateTimeWithYearOffset(date, yearOffset) {
  const shiftedDate = new Date(
    Date.UTC(date.gy + yearOffset, date.gm - 1, date.gd, date.hour, date.minute),
  );

  return {
    gy: shiftedDate.getUTCFullYear(),
    gm: shiftedDate.getUTCMonth() + 1,
    gd: shiftedDate.getUTCDate(),
    hour: date.hour,
    minute: date.minute,
  };
}

function getComparableDateTimeValue({ gy, gm, gd, hour, minute }) {
  return Date.UTC(gy, gm - 1, gd, hour, minute);
}

function getGregorianRange({
  minYearsAgo = 120,
  maxYearsFromNow = 0,
  minDateValue,
  maxDateValue,
} = {}) {
  const today = getDateTimeFormatterParts(new Date());
  const fallbackMinDate = getDateTimeWithYearOffset(
    {
      ...today,
      hour: 0,
      minute: 0,
    },
    -minYearsAgo,
  );
  const fallbackMaxDate = getDateTimeWithYearOffset(today, maxYearsFromNow);
  const parsedMinDate = parseValueAsTehranDateTime(minDateValue);
  const parsedMaxDate = parseValueAsTehranDateTime(maxDateValue);
  const minDate = parsedMinDate || fallbackMinDate;
  const maxDate = parsedMaxDate || fallbackMaxDate;

  if (
    getComparableDateTimeValue(maxDate) < getComparableDateTimeValue(minDate)
  ) {
    return {
      minDate: maxDate,
      maxDate: minDate,
    };
  }

  return {
    maxDate,
    minDate,
  };
}

function getInitialJalaliDateTime(value, fallbackDate, minDate, maxDate) {
  const jalaliDate = convertGregorianToJalaliDateTime(value);

  if (!jalaliDate) {
    return { ...fallbackDate };
  }

  return clampJalaliDateTime(jalaliDate, minDate, maxDate);
}

function formatJalaliLabel(value) {
  const jalaliDate = convertGregorianToJalaliDateTime(value);

  if (!jalaliDate) return "انتخاب تاریخ ";

  return `${toPersianDigits(jalaliDate.jd)} ${
    MONTH_NAMES[jalaliDate.jm - 1]
  } ${toPersianDigits(jalaliDate.jy)} - ${toPersianDigits(
    String(jalaliDate.hour).padStart(2, "0"),
  )}:${toPersianDigits(String(jalaliDate.minute).padStart(2, "0"))}`;
}

function convertTehranDateTimeToUtcIso({ gy, gm, gd, hour, minute }) {
  const targetTimestamp = Date.UTC(gy, gm - 1, gd, hour, minute);
  let utcTimestamp = targetTimestamp;

  for (let iteration = 0; iteration < 4; iteration += 1) {
    const zonedParts = getDateTimeFormatterParts(new Date(utcTimestamp));
    const zonedTimestamp = Date.UTC(
      zonedParts.gy,
      zonedParts.gm - 1,
      zonedParts.gd,
      zonedParts.hour,
      zonedParts.minute,
    );
    const difference = targetTimestamp - zonedTimestamp;

    if (difference === 0) {
      break;
    }

    utcTimestamp += difference;
  }

  return new Date(utcTimestamp).toISOString();
}

function WheelPickerColumn({ options, selectedValue, onChange }) {
  const scrollRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const selectedIndex = Math.max(
    options.findIndex((option) => option.value === selectedValue),
    0,
  );

  useEffect(() => {
    const scrollNode = scrollRef.current;

    if (!scrollNode) return;

    scrollNode.scrollTo({
      top: selectedIndex * ITEM_HEIGHT,
    });
  }, [selectedIndex, options.length]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const syncValueWithScroll = () => {
    const scrollNode = scrollRef.current;

    if (!scrollNode || options.length === 0) return;

    const nextIndex = Math.min(
      Math.max(Math.round(scrollNode.scrollTop / ITEM_HEIGHT), 0),
      options.length - 1,
    );
    const nextValue = options[nextIndex]?.value;

    if (nextValue !== undefined && nextValue !== selectedValue) {
      onChange(nextValue);
    }
  };

  return (
    <div className="relative flex-1">
      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-11 -translate-y-1/2 rounded-lg border border-zi-border-light bg-zi-bg1-light/50 dark:border-zi-border-dark dark:bg-zi-bg1-dark/50" />
      <div
        ref={scrollRef}
        onScroll={() => {
          if (scrollTimeoutRef.current) {
            window.clearTimeout(scrollTimeoutRef.current);
          }

          scrollTimeoutRef.current = window.setTimeout(syncValueWithScroll, 80);
        }}
        className="h-[220px] overflow-y-auto snap-y snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <div style={{ height: `${EDGE_SPACER_HEIGHT}px` }} />
        {options.map((option) => {
          const isSelected = option.value === selectedValue;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(option.value)}
              className={`flex h-11 w-full snap-center items-center justify-center px-3 text-sm transition ${
                isSelected
                  ? "relative font-medium text-zi-text-light dark:text-zi-text-dark"
                  : "text-zi-text-light/50 dark:text-zi-text-dark/50"
              }`}
            >
              {option.label}
            </button>
          );
        })}
        <div style={{ height: `${EDGE_SPACER_HEIGHT}px` }} />
      </div>
    </div>
  );
}

export default function BirthDatePicker({
  label = "",
  value,
  onChange,
  isReadOnly = true,
  minYearsAgo = 120,
  maxYearsFromNow = 0,
  minDateValue,
  maxDateValue,
  variant = "default",
  placeholder,
  dialogTitle,
  dialogDescription = "تاریخ و ساعت را براساس منطقه زمانی تهران انتخاب کنید.",
  helperText = "زمان در تهران نمایش داده می‌شود و هنگام ذخیره به UTC برای بک‌اند تبدیل می‌شود.",
}) {
  const { minDate: minGregorianDate, maxDate: maxGregorianDate } =
    getGregorianRange({
      minYearsAgo,
      maxYearsFromNow,
      minDateValue,
      maxDateValue,
    });
  const pickerVariant = PICKER_VARIANTS[variant] || PICKER_VARIANTS.default;
  const resolvedPlaceholder = placeholder || `انتخاب ${label || "تاریخ"}`;
  const resolvedDialogTitle = dialogTitle || resolvedPlaceholder;
  const minJalaliDate = toJalaali(
    minGregorianDate.gy,
    minGregorianDate.gm,
    minGregorianDate.gd,
  );
  const minJalaliYear = minJalaliDate.jy;
  const minJalaliMonth = minJalaliDate.jm;
  const minJalaliDay = minJalaliDate.jd;
  const maxJalaliDate = toJalaali(
    maxGregorianDate.gy,
    maxGregorianDate.gm,
    maxGregorianDate.gd,
  );
  const maxJalaliYear = maxJalaliDate.jy;
  const maxJalaliMonth = maxJalaliDate.jm;
  const maxJalaliDay = maxJalaliDate.jd;
  const minJalaliDateTime = {
    jy: minJalaliYear,
    jm: minJalaliMonth,
    jd: minJalaliDay,
    hour: minGregorianDate.hour,
    minute: minGregorianDate.minute,
  };
  const maxJalaliDateTime = {
    jy: maxJalaliYear,
    jm: maxJalaliMonth,
    jd: maxJalaliDay,
    hour: maxGregorianDate.hour,
    minute: maxGregorianDate.minute,
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(() =>
    getInitialJalaliDateTime(
      value,
      maxJalaliDateTime,
      minJalaliDateTime,
      maxJalaliDateTime,
    ),
  );

  useEffect(() => {
    if (!isModalOpen) {
      const currentMinJalaliDateTime = {
        jy: minJalaliYear,
        jm: minJalaliMonth,
        jd: minJalaliDay,
        hour: minGregorianDate.hour,
        minute: minGregorianDate.minute,
      };
      const currentMaxJalaliDateTime = {
        jy: maxJalaliYear,
        jm: maxJalaliMonth,
        jd: maxJalaliDay,
        hour: maxGregorianDate.hour,
        minute: maxGregorianDate.minute,
      };

      setDraftDate(
        getInitialJalaliDateTime(
          value,
          currentMaxJalaliDateTime,
          currentMinJalaliDateTime,
          currentMaxJalaliDateTime,
        ),
      );
    }
  }, [
    value,
    isModalOpen,
    minJalaliYear,
    minJalaliMonth,
    minJalaliDay,
    minGregorianDate.hour,
    minGregorianDate.minute,
    maxJalaliYear,
    maxJalaliMonth,
    maxJalaliDay,
    maxGregorianDate.hour,
    maxGregorianDate.minute,
  ]);

  useEffect(() => {
    if (!isReadOnly || !isModalOpen) return;

    setIsModalOpen(false);
  }, [isReadOnly, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const yearOptions = [];

  for (
    let year = maxJalaliDateTime.jy;
    year >= minJalaliDateTime.jy;
    year -= 1
  ) {
    yearOptions.push({
      value: year,
      label: toPersianDigits(year),
    });
  }

  const minMonth =
    draftDate.jy === minJalaliDateTime.jy ? minJalaliDateTime.jm : 1;
  const maxMonth =
    draftDate.jy === maxJalaliDateTime.jy ? maxJalaliDateTime.jm : 12;
  const monthOptions = [];

  for (let month = minMonth; month <= maxMonth; month += 1) {
    monthOptions.push({
      value: month,
      label: MONTH_NAMES[month - 1],
    });
  }

  const minDay =
    draftDate.jy === minJalaliDateTime.jy &&
    draftDate.jm === minJalaliDateTime.jm
      ? minJalaliDateTime.jd
      : 1;
  const maxDay =
    draftDate.jy === maxJalaliDateTime.jy &&
    draftDate.jm === maxJalaliDateTime.jm
      ? Math.min(
          maxJalaliDateTime.jd,
          jalaaliMonthLength(draftDate.jy, draftDate.jm),
        )
      : jalaaliMonthLength(draftDate.jy, draftDate.jm);
  const dayOptions = [];

  for (let day = minDay; day <= maxDay; day += 1) {
    dayOptions.push({
      value: day,
      label: toPersianDigits(String(day).padStart(2, "0")),
    });
  }

  const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
    value: hour,
    label: toPersianDigits(String(hour).padStart(2, "0")),
  }));
  const minuteOptions = Array.from({ length: 60 }, (_, minute) => ({
    value: minute,
    label: toPersianDigits(String(minute).padStart(2, "0")),
  }));
  const fieldClassName =
    `${pickerVariant.buttonBaseClassName} ` +
    (isReadOnly
      ? pickerVariant.readOnlyButtonClassName
      : pickerVariant.editableButtonClassName);

  const openPicker = () => {
    if (isReadOnly) return;

    setDraftDate(
      getInitialJalaliDateTime(
        value,
        maxJalaliDateTime,
        minJalaliDateTime,
        maxJalaliDateTime,
      ),
    );
    setIsModalOpen(true);
  };

  const updateDraftDate = (nextDate) => {
    setDraftDate((previousDate) =>
      clampJalaliDateTime(
        { ...previousDate, ...nextDate },
        minJalaliDateTime,
        maxJalaliDateTime,
      ),
    );
  };

  const handleSubmit = () => {
    const gregorianDate = toGregorian(draftDate.jy, draftDate.jm, draftDate.jd);

    onChange?.(
      convertTehranDateTimeToUtcIso({
        ...gregorianDate,
        hour: draftDate.hour,
        minute: draftDate.minute,
      }),
    );
    setIsModalOpen(false);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <div className={pickerVariant.wrapperClassName}>
        <label className={pickerVariant.labelClassName}>
          {label}
        </label>
        <button
          type="button"
          aria-disabled={isReadOnly}
          tabIndex={isReadOnly ? -1 : 0}
          onClick={openPicker}
          className={fieldClassName}
        >
          {value ? (
            formatJalaliLabel(value)
          ) : (
            <span
              className={
                isReadOnly ? "opacity-0" : pickerVariant.placeholderClassName
              }
            >
              {resolvedPlaceholder}
            </span>
          )}
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
            <SVG
              name="calendar"
              classname="stroke-zi-text-light dark:stroke-zi-text-dark fill-zi-text-light dark:fill-zi-text-dark"
            />
          </span>
        </button>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
          onClick={handleOverlayClick}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-md rounded-2xl bg-zi-bg2-light p-5 shadow-md dark:bg-zi-bg2-dark"
          >
            <div className="mb-5 text-right">
              <h3 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
                {resolvedDialogTitle}
              </h3>
              <p className="mt-1 text-sm text-zi-text-light/50 dark:text-zi-text-dark/50">
                {dialogDescription}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <WheelPickerColumn
                options={dayOptions}
                selectedValue={draftDate.jd}
                onChange={(day) => updateDraftDate({ jd: day })}
              />
              <WheelPickerColumn
                options={monthOptions}
                selectedValue={draftDate.jm}
                onChange={(month) => updateDraftDate({ jm: month })}
              />
              <WheelPickerColumn
                options={yearOptions}
                selectedValue={draftDate.jy}
                onChange={(year) => updateDraftDate({ jy: year })}
              />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <WheelPickerColumn
                options={minuteOptions}
                selectedValue={draftDate.minute}
                onChange={(minute) => updateDraftDate({ minute })}
              />
              <WheelPickerColumn
                options={hourOptions}
                selectedValue={draftDate.hour}
                onChange={(hour) => updateDraftDate({ hour })}
              />
            </div>

            <div className="mt-5 rounded-lg bg-zi-bg1-light px-4 py-2 text-center text-sm text-zi-text-light dark:bg-zi-bg1-dark dark:text-zi-text-dark">
              {toPersianDigits(draftDate.jd)} {MONTH_NAMES[draftDate.jm - 1]}{" "}
              {toPersianDigits(draftDate.jy)} -{" "}
              {toPersianDigits(String(draftDate.hour).padStart(2, "0"))}:
              {toPersianDigits(String(draftDate.minute).padStart(2, "0"))}
            </div>

            {helperText && (
              <div className="mt-3 rounded-lg border border-zi-border-light/60 px-4 py-2 text-center text-xs text-zi-text-light/60 dark:border-zi-border-dark/60 dark:text-zi-text-dark/60">
                {helperText}
              </div>
            )}

            <div className="mt-5 flex items-center justify-end gap-3">
              <Custom
                text="بستن"
                onClick={() => setIsModalOpen(false)}
                className="w-full border border-zi-border-dark !text-sm !rounded-lg !py-1.5 !px-3 bg-zi-bg1-light dark:bg-zi-bg1-dark text-zi-text-light dark:text-zi-text-dark hocus:ring-1 hocus:ring-zi-border-dark"
              />
              <Custom
                text="ثبت"
                onClick={handleSubmit}
                className="w-full border border-zi-border-dark !text-sm !rounded-lg !py-1.5 !px-3 bg-zi-gradient-button-blue text-zi-text-dark hocus:ring-1 hocus:ring-offset-1 hocus:ring-zi-border-dark"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
